import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { createProspect, updateProspect } from "../../../api/database";
import { categoryOptionValue, isCategoryOption, resolveListChoice } from "../emptyCategories";
import AddressAutocomplete from "../../../components/common/AddressAutocomplete";

// AU states, used to pull the suburb out of a picked address like
// "60 Kernan Street, Strathmore VIC 3041".
const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];
const titleCase = (s) =>
  s.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());

const parseAddressParts = (str) => {
  if (!str) return { suburb: "", state: "", postcode: "" };
  const parts = String(str).split(",").map((s) => s.trim()).filter(Boolean);
  const last = parts[parts.length - 1] || "";
  let postcode = "", state = "";
  const suburbTokens = [];
  last.split(/\s+/).forEach((t) => {
    if (/^\d{4}$/.test(t)) postcode = t;
    else if (AU_STATES.includes(t.toUpperCase())) state = t.toUpperCase();
    else suburbTokens.push(t);
  });
  return { suburb: titleCase(suburbTokens.join(" ")), state, postcode };
};

/**
 * "Add record" form whose fields are driven by the columns currently shown in
 * the grid (including custom columns, and respecting hidden/renamed ones). Each
 * column key is routed to the right place in the payload via FIELD_META; custom
 * columns go into custom_fields; derived columns (agent, next call) are skipped.
 */
const FIELD_META = {
  // record-level
  name: { target: "record", input: "text" },
  mobile: { target: "record", input: "text" },
  email: { target: "record", input: "text" },
  // Second person on the row (joint owners / a couple buying together). Typing
  // two names or two numbers into the primary boxes also splits into these.
  second_name: { target: "record", input: "text" },
  second_mobile: { target: "record", input: "text" },
  second_email: { target: "record", input: "text" },
  lead_temp: { target: "record", input: "temp" },
  timeframe: { target: "record", input: "text" },
  // owner profile
  address: { target: "owner", input: "text" },
  suburb: { target: "owner", input: "text" },
  property_type: { target: "owner", input: "text" },
  beds: { target: "owner", input: "number" },
  baths: { target: "owner", input: "number" },
  car: { target: "owner", input: "number" },
  land_sqm: { target: "owner", input: "number" },
  last_sold_price: { target: "owner", input: "number" },
  last_sold_date: { target: "owner", input: "date" },
  // buyer profile
  buyer_type: { target: "buyer", input: "text" }, // free-text segment, e.g. "Blocks"
  budget: { target: "buyer", input: "number" },
  target_suburbs: { target: "buyer", input: "suburbs" },
  wanted_type: { target: "buyer", input: "text" },
  beds_min: { target: "buyer", input: "number" },
  // derived / not settable here
  agent_name: { target: "skip" },
  next_call_at: { target: "skip" },
};

const metaFor = (col) => {
  if (col.custom) {
    const input = col.type === "money" || col.type === "number" ? "number" : col.type === "date" ? "date" : "text";
    return { target: "custom", input };
  }
  return FIELD_META[col.key] || { target: "skip" };
};

export default function AddRecordModal({ view, columns, lists, emptyCategories = [], activeList, record, agent, onClose, onCreated }) {
  const isOwner = view === "owners";
  const isEdit = !!record;

  // Only the columns we can actually edit (skip derived ones).
  const editable = useMemo(
    () => (columns || []).filter((c) => metaFor(c).target !== "skip"),
    [columns]
  );

  // Read a column's current value off an existing record (edit mode).
  const valueFromRecord = (c) => {
    const meta = metaFor(c);
    let src;
    if (meta.target === "record") src = record[c.key];
    else if (meta.target === "owner") src = record.owner_profile?.[c.key];
    else if (meta.target === "buyer") src = record.buyer_profile?.[c.key];
    else if (meta.target === "custom") src = record.custom_fields?.[c.key];
    if (src === null || src === undefined) return c.key === "lead_temp" ? "cold" : "";
    if (meta.input === "suburbs" && Array.isArray(src)) return src.join(", ");
    return String(src);
  };

  const [busy, setBusy] = useState(false);
  const [vals, setVals] = useState(() => {
    const init = {};
    editable.forEach((c) => {
      if (isEdit) init[c.key] = valueFromRecord(c);
      else if (c.key === "lead_temp") init[c.key] = "cold";
      else init[c.key] = "";
    });
    return init;
  });
  const [list, setList] = useState(
    isEdit ? (record.list ? String(record.list) : "") : activeList && activeList !== "all" ? String(activeList) : ""
  );

  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const submit = async () => {
    // Require at least some identifying value.
    const hasIdentity =
      (vals.name || "").trim() ||
      (vals.mobile || "").trim() ||
      (vals.email || "").trim() ||
      (isOwner && (vals.address || "").trim());
    if (!hasIdentity) {
      toast.error(isOwner ? "Enter at least an address, name or contact" : "Enter at least a name or contact");
      return;
    }

    const payload = { record_type: isOwner ? "owner" : "buyer", list: list || null };
    const ownerP = {};
    const buyerP = {};
    // Seed custom_fields from the existing record so hidden custom columns aren't wiped.
    const custom = isEdit ? { ...(record.custom_fields || {}) } : {};
    editable.forEach((c) => {
      const meta = metaFor(c);
      let v = vals[c.key];
      if (meta.input === "number") v = num(v);
      else if (meta.input === "suburbs") v = v ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];
      else if (typeof v === "string") v = v.trim();
      const bucket = { record: payload, owner: ownerP, buyer: buyerP, custom }[meta.target];
      if (bucket) bucket[c.key] = v;
    });
    if (isOwner) payload.owner_profile = ownerP;
    else payload.buyer_profile = buyerP;
    if (Object.keys(custom).length) payload.custom_fields = custom;

    setBusy(true);
    try {
      // A brand-new empty category becomes a list the moment it is picked.
      if (isCategoryOption(list)) payload.list = await resolveListChoice(view, list);
      if (isEdit) await updateProspect(record.id, payload);
      // `agent` files the row under the owner of the shared Database being
      // viewed, so it doesn't vanish from the grid the moment it saves.
      else await createProspect(payload, agent);
      toast.success(isEdit ? "Record updated" : "Record added");
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? "Couldn't update record" : "Couldn't add record"));
    } finally {
      setBusy(false);
    }
  };

  const renderInput = (c) => {
    const meta = metaFor(c);
    const v = vals[c.key] ?? "";
    // Address gets the same live-suggestion field as Add Property; picking a
    // suggestion fills the address and auto-populates the Suburb column.
    if (c.key === "address") {
      return (
        <AddressAutocomplete
          value={v}
          onChange={(e) => set("address", e.target.value)}
          onSelect={(sug) => {
            const raw = sug.raw || {};
            const comp = raw.components || raw.address_components || {};
            const desc = sug.description || "";
            let suburb =
              raw.suburb || raw.locality || comp.suburb || comp.locality || "";
            if (!suburb) suburb = parseAddressParts(desc).suburb;
            set("address", desc);
            if (suburb) set("suburb", suburb);
          }}
          placeholder="Start typing address…"
        />
      );
    }
    if (meta.input === "temp") {
      return (
        <select className={inputCls} value={v} onChange={(e) => set(c.key, e.target.value)}>
          <option value="cold">Cold</option>
          <option value="warm">Warm</option>
          <option value="hot">Hot</option>
        </select>
      );
    }
    if (meta.input === "number") {
      return <input type="number" className={inputCls} value={v} onChange={(e) => set(c.key, e.target.value)} />;
    }
    if (meta.input === "date") {
      return <input type="date" className={inputCls} value={v} onChange={(e) => set(c.key, e.target.value)} />;
    }
    if (meta.input === "suburbs") {
      return <input className={inputCls} placeholder="comma separated" value={v} onChange={(e) => set(c.key, e.target.value)} />;
    }
    const placeholders = {
      buyer_type: "e.g. Blocks",
      second_name: "Joint owner, if there is one",
      second_mobile: "Their own mobile",
      second_email: "Their own email",
    };
    return (
      <input
        className={inputCls}
        placeholder={placeholders[c.key]}
        value={v}
        onChange={(e) => set(c.key, e.target.value)}
      />
    );
  };

  // Wide fields span both columns for readability.
  const wide = new Set(["address", "target_suburbs", "timeframe", "name", "second_name"]);

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[560px] max-w-full max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">{isEdit ? "Edit" : "Add"} {isOwner ? "owner" : "buyer"}</h3>
          <button onClick={onClose} className="text-gray-500">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-auto grid grid-cols-2 gap-3">
          {editable.map((c) => (
            <label key={c.key} className={`flex flex-col gap-1 ${wide.has(c.key) ? "col-span-2" : ""}`}>
              <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">{c.label}</span>
              {renderInput(c)}
            </label>
          ))}

          {/* List is a grouping (chips), not a column — always offered. */}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">List</span>
            <select className={inputCls} value={list} onChange={(e) => setList(e.target.value)}>
              <option value="">— none —</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
              {/* New categories with nothing in them yet (CRM-2026-00099). */}
              {emptyCategories.map((t) => (
                <option key={`cat-${t}`} value={categoryOptionValue(t)}>{t}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="text-sm font-semibold text-gray-600">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold disabled:opacity-50">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add record"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#55c5d0]";
